import type {
  BankAccountVerifyRequestAccountTypeEnum,
  BankAccountVerifyResponse,
} from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchStipulationsApi } from "../../../api/client";
import { stipulationKeys } from "./stipulationKeys";

export interface VerifyBankAccountInput {
  approvalId: string;
  applicantPk: number;
  stipCode: string;
  routingNumber: string;
  accountNumber: string;
  accountType: BankAccountVerifyRequestAccountTypeEnum;
}

/**
 * Mutation hook for the manual ESS/GIACT bank-account verification endpoint.
 * Not idempotent server-side (each call spends a paid vendor attempt), so
 * callers must gate retries themselves. Invalidates the stips list on success
 * since caps2 may close a proof-of-residence stip as an undocumented side effect.
 */
export function useVerifyBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: VerifyBankAccountInput): Promise<BankAccountVerifyResponse> => {
      const response = await workbenchStipulationsApi.verifyBankAccount(
        input.approvalId,
        input.applicantPk,
        input.stipCode,
        {
          routingNumber: input.routingNumber,
          accountNumber: input.accountNumber,
          accountType: input.accountType,
        }
      );
      return response.data;
    },
    onSuccess: (_response, input) => {
      void queryClient.invalidateQueries({ queryKey: stipulationKeys.listsForApproval(input.approvalId) });
    },
  });
}
